import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Images,
  LayoutTemplate,
  Loader2,
  type LucideIcon,
  Palette,
  PanelsTopLeft,
  PenLine,
  Save,
  Settings2,
  Shapes,
  Trash2,
  Type,
  Wallpaper,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import { ImageLibrary } from './storefront/ImageLibrary'
import { SectionsEditor } from './storefront/SectionsEditor'
import { SeoPreview } from '@/components/admin/storefront/SeoPreview'
import { IdentityCard, type ThemeMode } from './storefront/IdentityCard'
import { LivePreview } from './storefront/LivePreview'
import { MockStorefront } from './storefront/MockStorefront'
import {
  BackgroundPanel,
  ColoursPanel,
  FontsPanel,
  LayoutPanel,
  ShapePanel,
  ThemeGallery,
} from './storefront/StylePanels'
import {
  fontPreviewHref,
  lookMeta,
  MAX_STOREFRONT_IMAGES,
  newImageId,
  normalizeSections,
  previewUrl,
  readConfig,
  readImages,
  type StorefrontImage,
  type StorefrontLook,
  type StorefrontSection,
  type StorefrontSocial,
} from './storefront/storefrontConfig'

/**
 * Admin → Website → Design.
 *
 * A design studio: pick one of twelve themes, then make it yours — colours,
 * page colour, fonts, corners, button shape, background photo or pattern,
 * layout, photos, sections and copy. One section of controls is open at a
 * time in a narrow column, and the rest of the room goes to the tenant's real
 * storefront, restyled live. Nothing reaches customers until Save.
 *
 * Writes the discrete `storefront_*` keys plus the consolidated
 * `storefront_config` blob, the `storefront_images` library and the
 * `storefront_background_image` to app_settings; the storefront reads them at
 * runtime (GET /public/settings) and the backend sanitizes every value on
 * read, so a stale bundle or hand-edited row can never break the page.
 */
const HEX = /^#[0-9a-fA-F]{6}$/
const DEFAULT_BRAND = '#16a34a'
const DEFAULT_ACCENT = '#f97316'
const BASE_URL_KEY = 'storefront_base_url'

// Mirror of the backend socialPlatforms allowlist (public_storefront.go).
const SOCIAL_PLATFORMS = ['instagram', 'facebook', 'tiktok', 'whatsapp', 'x', 'youtube', 'website']

function str(d: Record<string, unknown> | undefined, key: string): string {
  const v = d?.[key]
  return typeof v === 'string' ? v : ''
}

// Narrow a stored value to an allowlist, '' (the theme's default) otherwise.
const oneOf =
  <T extends string>(allowed: readonly T[]) =>
  (v: unknown): T | '' =>
    typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : ''
const asLook = oneOf([
  'counter',
  'bakery',
  'dhaba',
  'roastery',
  'night',
  'fresh',
  'heritage',
  'atelier',
  'pop',
  'shelf',
  'docket',
  'bistro',
] as const)
const asRadius = oneOf(['sharp', 'soft', 'pill'] as const)
const asMenuLayout = oneOf(['grid', 'list', 'tiles', 'board', 'magazine', 'gallery', 'shelf', 'rows'] as const)
const asHero = oneOf([
  'split',
  'cover',
  'type',
  'minimal',
  'carousel',
  'poster',
  'collage',
  'fullbleed',
  'billboard',
  'lineup',
  'receipt',
  'side',
] as const)
const asCardStyle = oneOf(['raised', 'outline', 'flat', 'bare', 'sticker'] as const)
const asNavStyle = oneOf(['pills', 'tabs', 'index'] as const)
const asTexture = oneOf(['none', 'grain', 'paper'] as const)
const asButtonShape = oneOf(['pill', 'rounded', 'square'] as const)
const asHeadingCase = oneOf(['normal', 'upper'] as const)
const asBgFit = oneOf(['cover', 'tile'] as const)
const asBgStrength = oneOf(['faint', 'soft', 'strong'] as const)
const asHex = (v: unknown): string => (typeof v === 'string' && HEX.test(v) ? v : '')

// The editor's sections, in the order a tenant works through them.
type PanelId = 'theme' | 'colours' | 'fonts' | 'shape' | 'background' | 'layout' | 'images' | 'page' | 'details'
const PANELS: Array<{
  group: string
  items: Array<{ id: PanelId; label: string; hint: string; icon: LucideIcon }>
}> = [
  {
    group: 'Look',
    items: [
      { id: 'theme', label: 'Theme', hint: 'The whole character of your site. Start here.', icon: LayoutTemplate },
      { id: 'colours', label: 'Logo and colours', hint: 'Your colours go on top of the theme.', icon: Palette },
      { id: 'fonts', label: 'Fonts', hint: 'Each name is set in its own face.', icon: Type },
      { id: 'shape', label: 'Shapes', hint: 'Corners and buttons are separate, so you can pair soft cards with square buttons.', icon: Shapes },
      { id: 'background', label: 'Background', hint: 'A photo or pattern behind the whole page, under a veil of your page colour.', icon: Wallpaper },
    ],
  },
  {
    group: 'Page',
    items: [
      { id: 'layout', label: 'Layout', hint: 'How the top of the page and the menu are arranged.', icon: PanelsTopLeft },
      {
        id: 'images',
        label: 'Photos',
        hint: `Up to ${MAX_STOREFRONT_IMAGES} photos of your food or space. The first is the main photo and what shows when your link is shared on WhatsApp. Drag to reorder.`,
        icon: Images,
      },
      { id: 'page', label: 'Words and sections', hint: 'What the page says, what it shows and in what order.', icon: PenLine },
    ],
  },
  {
    group: 'Site',
    items: [{ id: 'details', label: 'Details', hint: 'Home-screen icon, link preview, minimum order and social links.', icon: Settings2 }],
  },
]
const PANEL_LIST = PANELS.flatMap((g) => g.items)

// The design choices a theme sets and "Reset to the theme" clears.
interface Tuning {
  radius: string
  menuLayout: string
  heroVariant: string
  cardStyle: string
  navStyle: string
  texture: string
  buttonShape: string
  headingCase: string
  canvasLight: string
  canvasDark: string
  fontHeading: string
  fontBody: string
}

export function AdminStorefrontAppearance() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const settingsQuery = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
  })
  const d = settingsQuery.data?.data as Record<string, unknown> | undefined

  // Discrete storefront_* keys (empty ⇒ inherit receipt branding).
  const [brand, setBrand] = useState('')
  const [accent, setAccent] = useState('')
  const [logo, setLogo] = useState('')
  const [hero, setHero] = useState('') // legacy single banner, still written back
  const [mode, setMode] = useState<ThemeMode>('')
  const [images, setImages] = useState<StorefrontImage[]>([])
  const [minOrder, setMinOrder] = useState('')
  const [bgImage, setBgImage] = useState('')
  const [base, setBase] = useState('')

  // storefront_config blob. '' on any design axis ⇒ the theme's own value.
  const [look, setLook] = useState<StorefrontLook>('')
  const [radius, setRadius] = useState('')
  const [menuLayout, setMenuLayout] = useState('')
  const [heroVariant, setHeroVariant] = useState('')
  const [cardStyle, setCardStyle] = useState('')
  const [navStyle, setNavStyle] = useState('')
  const [texture, setTexture] = useState('')
  const [buttonShape, setButtonShape] = useState('')
  const [headingCase, setHeadingCase] = useState('')
  const [canvasLight, setCanvasLight] = useState('')
  const [canvasDark, setCanvasDark] = useState('')
  const [bgFit, setBgFit] = useState('')
  const [bgStrength, setBgStrength] = useState('')
  const [fontHeading, setFontHeading] = useState('')
  const [fontBody, setFontBody] = useState('')
  const [sections, setSections] = useState<StorefrontSection[]>(normalizeSections(null))
  const [announcementText, setAnnouncementText] = useState('')
  const [announcementUrl, setAnnouncementUrl] = useState('')
  const [heroHeadline, setHeroHeadline] = useState('')
  const [heroSubcopy, setHeroSubcopy] = useState('')
  const [about, setAbout] = useState('')
  const [splashPhrase, setSplashPhrase] = useState('')
  const [seoTitle, setSeoTitle] = useState('')
  const [seoDescription, setSeoDescription] = useState('')
  const [pwaShortName, setPwaShortName] = useState('')
  const [appIconUrl, setAppIconUrl] = useState('')
  const [socials, setSocials] = useState<StorefrontSocial[]>([])

  // "Unsaved changes": a snapshot of everything Save writes, taken once the
  // loaded values have landed in state, compared on every render.
  const [baseline, setBaseline] = useState<string | null>(null)
  // Bumped by the load effect; the baseline is taken on the render where the
  // loaded values have landed in state, never the render before it.
  const [loadTick, setLoadTick] = useState(0)

  // Editor chrome: which section is open, and (when the studio is too narrow
  // for both) whether the controls or the preview are showing.
  const [panel, setPanel] = useState<PanelId>('theme')
  const [view, setView] = useState<'edit' | 'preview'>('edit')
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  // The theme they switched away from, with its fine-tuning, for "Go back".
  const [previous, setPrevious] = useState<{ look: StorefrontLook; tuning: Tuning } | null>(null)

  const load = (d: Record<string, unknown> | undefined) => {
    if (!d) return
    setBrand(str(d, 'storefront_theme_color'))
    setAccent(str(d, 'storefront_accent_color'))
    setLogo(str(d, 'storefront_logo_url'))
    setHero(str(d, 'storefront_hero_url'))
    const m = str(d, 'storefront_theme_mode')
    setMode(m === 'light' || m === 'dark' || m === 'system' ? m : '')
    setImages(readImages(d['storefront_images']))
    setBgImage(str(d, 'storefront_background_image'))
    setBase(str(d, BASE_URL_KEY))

    const cfg = readConfig(d['storefront_config'])
    setLook(asLook(cfg.look))
    setRadius(asRadius(cfg.radius))
    setMenuLayout(asMenuLayout(cfg.menu_layout))
    setHeroVariant(asHero(cfg.hero_variant))
    setCardStyle(asCardStyle(cfg.card_style))
    setNavStyle(asNavStyle(cfg.nav_style))
    setTexture(asTexture(cfg.texture))
    setButtonShape(asButtonShape(cfg.button_shape))
    setHeadingCase(asHeadingCase(cfg.heading_case))
    setCanvasLight(asHex(cfg.canvas_light))
    setCanvasDark(asHex(cfg.canvas_dark))
    setBgFit(asBgFit(cfg.background_fit))
    setBgStrength(asBgStrength(cfg.background_strength))
    const secs = normalizeSections(cfg.sections, cfg.layout)
    // reviews.enabled is the authoritative opt-in; keep the row in step with it.
    if (cfg.reviews && typeof cfg.reviews.enabled === 'boolean') {
      const on = cfg.reviews.enabled
      secs.forEach((s) => {
        if (s.id === 'reviews') s.enabled = on
      })
    }
    setSections(secs)
    setAnnouncementText(cfg.announcement?.text ?? '')
    setAnnouncementUrl(cfg.announcement?.url ?? '')
    setHeroHeadline(cfg.hero_headline ?? '')
    setHeroSubcopy(cfg.hero_subcopy ?? '')
    setAbout(cfg.about ?? '')
    setFontHeading(cfg.font_heading ?? '')
    setFontBody(cfg.font_body ?? '')
    setSplashPhrase(cfg.splash_phrase ?? '')
    setSeoTitle(cfg.seo_title ?? '')
    setSeoDescription(cfg.seo_description ?? '')
    setPwaShortName(cfg.pwa_short_name ?? '')
    setAppIconUrl(cfg.app_icon_url ?? '')
    setSocials(Array.isArray(cfg.socials) ? cfg.socials : [])
    const mv = d['storefront_min_order_value']
    setMinOrder(typeof mv === 'number' && mv > 0 ? String(mv) : '')
    setPrevious(null)
    setConfirmDiscard(false)
    setLoadTick((t) => t + 1)
  }
  useEffect(() => {
    load(d)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load only reads d
  }, [d])

  // Effective values: storefront key → receipt fallback → default.
  const effBrand = (HEX.test(brand) ? brand : str(d, 'receipt_accent_color')) || DEFAULT_BRAND
  const effAccent = (HEX.test(accent) ? accent : '') || DEFAULT_ACCENT
  const effLogo = logo.trim() || str(d, 'receipt_logo_url')
  const meta = lookMeta(look || 'counter')
  const previewDark = mode === 'dark' || (mode === '' && meta.darkFirst === true)
  const restaurantName = str(d, 'restaurant_name') || str(d, 'receipt_business_name') || 'Your restaurant'

  const brandInvalid = brand.trim() !== '' && !HEX.test(brand)
  const accentInvalid = accent.trim() !== '' && !HEX.test(accent)

  // Every face in the font picker, drawn in itself.
  useEffect(() => {
    const id = 'sf-font-previews'
    let link = document.getElementById(id) as HTMLLinkElement | null
    if (!link) {
      link = document.createElement('link')
      link.id = id
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    }
    link.href = fontPreviewHref(restaurantName)
  }, [restaurantName])

  const moveLegacyIntoLibrary = () => {
    const src = hero.trim()
    if (!src) return
    setImages([{ id: newImageId(), src, caption: '' }])
    setHero('')
  }

  // Choosing a theme starts it fresh: its own fonts, shapes, layout and page
  // colour. Brand colours, logo, photos and copy are the tenant's and stay.
  const resetFineTuning = () => {
    setRadius('')
    setMenuLayout('')
    setHeroVariant('')
    setCardStyle('')
    setNavStyle('')
    setTexture('')
    setButtonShape('')
    setHeadingCase('')
    setCanvasLight('')
    setCanvasDark('')
    setFontHeading('')
    setFontBody('')
  }
  const tuning: Tuning = { radius, menuLayout, heroVariant, cardStyle, navStyle, texture, buttonShape, headingCase, canvasLight, canvasDark, fontHeading, fontBody }
  const applyTuning = (t: Tuning) => {
    setRadius(t.radius)
    setMenuLayout(t.menuLayout)
    setHeroVariant(t.heroVariant)
    setCardStyle(t.cardStyle)
    setNavStyle(t.navStyle)
    setTexture(t.texture)
    setButtonShape(t.buttonShape)
    setHeadingCase(t.headingCase)
    setCanvasLight(t.canvasLight)
    setCanvasDark(t.canvasDark)
    setFontHeading(t.fontHeading)
    setFontBody(t.fontBody)
  }
  const chooseLook = (v: StorefrontLook) => {
    if ((v || 'counter') === (look || 'counter')) return
    setPrevious({ look, tuning })
    setLook(v)
    resetFineTuning()
  }
  const undoLook = () => {
    if (!previous) return
    setLook(previous.look)
    applyTuning(previous.tuning)
    setPrevious(null)
  }

  // Which sections differ from the theme's own, for the dots in the rail.
  const tuned: Partial<Record<PanelId, number>> = {
    colours: [canvasLight, canvasDark].filter(Boolean).length,
    fonts: [fontHeading, fontBody, headingCase].filter(Boolean).length,
    shape: [radius, buttonShape, cardStyle].filter(Boolean).length,
    background: [texture].filter(Boolean).length,
    layout: [heroVariant, menuLayout, navStyle].filter(Boolean).length,
  }
  const tunedTotal = Object.values(tuned).reduce((a, b) => a + (b ?? 0), 0)

  const buildConfig = () => {
    const reviewsOn = sections.some((s) => s.id === 'reviews' && s.enabled)
    const announce = announcementText.trim()
    return {
      hero_headline: heroHeadline.trim(),
      hero_subcopy: heroSubcopy.trim(),
      about: about.trim(),
      font_heading: fontHeading,
      font_body: fontBody,
      splash_phrase: splashPhrase.trim(),
      socials: socials
        .filter((s) => s.url.trim() !== '')
        .map((s) => ({ platform: s.platform, url: s.url.trim() })),
      // Old storefront bundles still read these two booleans.
      layout: {
        show_featured: sections.some((s) => s.id === 'featured' && s.enabled),
        show_deals: sections.some((s) => s.id === 'deals' && s.enabled),
      },
      look,
      radius,
      menu_layout: menuLayout,
      hero_variant: heroVariant,
      card_style: cardStyle,
      nav_style: navStyle,
      texture,
      button_shape: buttonShape,
      heading_case: headingCase,
      canvas_light: HEX.test(canvasLight) ? canvasLight : '',
      canvas_dark: HEX.test(canvasDark) ? canvasDark : '',
      background_fit: bgFit,
      background_strength: bgStrength,
      sections: sections.map((s) => ({ id: s.id, enabled: s.id === 'menu' ? true : s.enabled })),
      ...(announce
        ? { announcement: { text: announce.slice(0, 120), ...(announcementUrl.trim() ? { url: announcementUrl.trim() } : {}) } }
        : {}),
      reviews: { enabled: reviewsOn },
      seo_title: seoTitle.trim().slice(0, 70),
      seo_description: seoDescription.trim().slice(0, 160),
      pwa_short_name: pwaShortName.trim().slice(0, 12),
      app_icon_url: appIconUrl.trim(),
    }
  }

  const images4save = images.map((i) => ({ id: i.id, src: i.src, ...(i.caption?.trim() ? { caption: i.caption.trim().slice(0, 80) } : {}) }))
  const snapshot = JSON.stringify([
    buildConfig(),
    brand,
    accent,
    logo.trim(),
    hero.trim(),
    mode,
    images4save,
    minOrder,
    bgImage,
    base.trim(),
  ])
  useEffect(() => {
    if (loadTick > 0) setBaseline(snapshot)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on load
  }, [loadTick])
  const dirty = baseline !== null && snapshot !== baseline

  // Leaving with unsaved design work asks first.
  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const saveMutation = useMutation({
    mutationFn: async () => {
      await apiClient.updateSetting('storefront_theme_color', HEX.test(brand) ? brand : '')
      await apiClient.updateSetting('storefront_accent_color', HEX.test(accent) ? accent : '')
      await apiClient.updateSetting('storefront_logo_url', logo.trim())
      await apiClient.updateSetting('storefront_hero_url', hero.trim())
      await apiClient.updateSetting('storefront_theme_mode', mode)
      await apiClient.updateSetting('storefront_images', images4save)
      await apiClient.updateSetting('storefront_background_image', bgImage)
      await apiClient.updateSetting('storefront_config', buildConfig())
      await apiClient.updateSetting('storefront_min_order_value', Math.max(0, Number(minOrder) || 0))
      if (base.trim() !== str(d, BASE_URL_KEY)) await apiClient.updateSetting(BASE_URL_KEY, base.trim())
    },
    onSuccess: () => {
      toast({ title: 'Appearance saved', description: 'Customers see the new design on their next visit.' })
      setBaseline(snapshot)
      setPrevious(null)
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
      queryClient.invalidateQueries({ queryKey: ['settings', BASE_URL_KEY] })
    },
    onError: (err) => {
      toast({ title: 'Could not save', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    },
  })

  const canSave = dirty && !brandInvalid && !accentInvalid && !saveMutation.isPending

  // What the live preview renders: the unsaved design in /public/settings
  // field names. Unset axes go as '' so the theme's own value applies.
  const design = useMemo(
    () => ({
      look: look || 'counter',
      radius,
      menu_layout: menuLayout,
      hero_variant: heroVariant,
      card_style: cardStyle,
      nav_style: navStyle,
      texture,
      button_shape: buttonShape,
      heading_case: headingCase,
      font_heading: fontHeading,
      font_body: fontBody,
      background_fit: bgFit,
      background_strength: bgStrength,
      theme_mode: mode,
      theme_color: effBrand,
      accent_color: effAccent,
      canvas_light: HEX.test(canvasLight) ? canvasLight : '',
      canvas_dark: HEX.test(canvasDark) ? canvasDark : '',
      hero_headline: heroHeadline,
      hero_subcopy: heroSubcopy,
      about,
      logo_url: effLogo,
      background_image_url: bgImage,
      images: images.map((i) => ({ id: i.id, url: i.src, caption: i.caption })),
    }),
    [look, radius, menuLayout, heroVariant, cardStyle, navStyle, texture, buttonShape, headingCase, fontHeading, fontBody, bgFit, bgStrength, mode, effBrand, effAccent, canvasLight, canvasDark, heroHeadline, heroSubcopy, about, effLogo, bgImage, images],
  )
  const openUrl = previewUrl(base, {
    look,
    radius,
    menuLayout,
    heroVariant,
    cardStyle,
    navStyle,
    texture,
    fontHeading,
    fontBody,
    buttonShape,
    headingCase,
    brand: effBrand,
    accent: HEX.test(accent) ? accent : '',
    canvasLight,
    canvasDark,
  })

  const active = PANEL_LIST.find((p) => p.id === panel) ?? PANEL_LIST[0]

  return (
    <div
      id="storefront-appearance"
      className="flex min-h-0 flex-col overflow-hidden rounded-lg border bg-card @container lg:h-full"
    >
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2.5">
        <span className={cn('h-2 w-2 shrink-0 rounded-full', dirty ? 'bg-amber-500' : 'bg-emerald-500')} aria-hidden />
        <p className="min-w-0 flex-1 text-sm" role="status">
          {dirty ? (
            <>
              <span className="font-medium">Unsaved changes.</span>{' '}
              <span className="text-muted-foreground">Customers still see the saved design.</span>
            </>
          ) : (
            <span className="text-muted-foreground">This is the design your customers see.</span>
          )}
        </p>
        <div className="flex gap-1 rounded-lg bg-muted/60 p-1 @2xl:hidden" role="group" aria-label="Show">
          {(['edit', 'preview'] as const).map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={view === v}
              onClick={() => setView(v)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                view === v ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {v === 'edit' ? 'Edit' : 'Preview'}
            </button>
          ))}
        </div>
        {confirmDiscard ? (
          <>
            <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmDiscard(false)}>
              Keep editing
            </Button>
            <Button type="button" variant="destructive" size="sm" onClick={() => load(d)}>
              Discard changes
            </Button>
          </>
        ) : (
          <>
            {dirty && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmDiscard(true)} disabled={saveMutation.isPending}>
                Discard
              </Button>
            )}
            <Button type="button" size="sm" onClick={() => saveMutation.mutate()} disabled={!canSave} className="gap-1.5">
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {dirty ? 'Save changes' : 'Saved'}
            </Button>
          </>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col @2xl:flex-row">
        <nav
          aria-label="Design sections"
          className={cn(
            'no-scrollbar shrink-0 gap-1 overflow-x-auto border-b p-2 @2xl:flex @2xl:flex-col @2xl:overflow-y-auto @2xl:overflow-x-hidden @2xl:border-b-0 @2xl:border-r @5xl:w-52 @5xl:p-3',
            view === 'edit' ? 'flex' : 'hidden',
          )}
        >
          {PANELS.map((g, gi) => (
            <div key={g.group} className="contents">
              <div className={cn('hidden px-2.5 pb-1 text-xs font-medium text-muted-foreground @5xl:block', gi > 0 ? 'pt-4' : 'pt-1')}>
                {g.group}
              </div>
              {gi > 0 && <span className="mx-1 w-px shrink-0 self-stretch bg-border @2xl:mx-0 @2xl:my-1 @2xl:h-px @2xl:w-auto @5xl:hidden" aria-hidden />}
              {g.items.map((it) => {
                const Icon = it.icon
                const on = panel === it.id
                const changes = tuned[it.id] ?? 0
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => setPanel(it.id)}
                    aria-current={on ? 'page' : undefined}
                    title={it.label}
                    className={cn(
                      'relative flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-md px-2.5 py-2 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      on ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="@2xl:hidden @5xl:inline">{it.label}</span>
                    {changes > 0 && (
                      <span
                        className={cn(
                          'h-1.5 w-1.5 shrink-0 rounded-full @2xl:absolute @2xl:right-1.5 @2xl:top-1.5 @5xl:static @5xl:ml-auto',
                          on ? 'bg-primary-foreground' : 'bg-primary',
                        )}
                        title="Changed from the theme's own"
                      >
                        <span className="sr-only">Changed from the theme&rsquo;s own</span>
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          ))}
        </nav>

        <section
          key={panel}
          aria-label={active.label}
          className={cn(
            'min-h-0 min-w-0 flex-1 overflow-y-auto @2xl:block @2xl:w-[18rem] @2xl:flex-none @4xl:w-[22rem] @2xl:border-r @5xl:w-[24rem] @7xl:w-[26rem]',
            view === 'edit' ? 'block' : 'hidden',
          )}
        >
          <div className="space-y-5 p-5">
            <div>
              <h3 className="text-lg font-semibold leading-tight">{active.label}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{active.hint}</p>
            </div>

            {panel === 'theme' && (
              <ThemeGallery
                look={look}
                brand={effBrand}
                name={restaurantName}
                onLook={chooseLook}
                tuned={tunedTotal}
                onResetTuning={resetFineTuning}
                previous={previous ? lookMeta(previous.look || 'counter').label : null}
                onUndo={undoLook}
              />
            )}

            {panel === 'colours' && (
              <ColoursPanel
                meta={meta}
                brand={brand}
                accent={accent}
                canvasLight={canvasLight}
                canvasDark={canvasDark}
                mode={mode}
                brandPlaceholder={str(d, 'receipt_accent_color') || DEFAULT_BRAND}
                accentPlaceholder={DEFAULT_ACCENT}
                onBrand={setBrand}
                onAccent={setAccent}
                onCanvasLight={setCanvasLight}
                onCanvasDark={setCanvasDark}
                onMode={setMode}
                logo={logo}
                logoPlaceholder={str(d, 'receipt_logo_url')}
                onLogo={setLogo}
              />
            )}

            {panel === 'fonts' && (
              <FontsPanel
                meta={meta}
                heading={fontHeading}
                body={fontBody}
                headingCase={headingCase}
                sample={restaurantName}
                onHeading={setFontHeading}
                onBody={setFontBody}
                onHeadingCase={setHeadingCase}
              />
            )}

            {panel === 'shape' && (
              <ShapePanel
                meta={meta}
                radius={radius}
                buttonShape={buttonShape}
                cardStyle={cardStyle}
                brand={effBrand}
                onRadius={setRadius}
                onButtonShape={setButtonShape}
                onCardStyle={setCardStyle}
              />
            )}

            {panel === 'background' && (
              <BackgroundPanel
                meta={meta}
                texture={texture}
                image={bgImage}
                fit={bgFit}
                strength={bgStrength}
                onTexture={setTexture}
                onImage={setBgImage}
                onFit={setBgFit}
                onStrength={setBgStrength}
              />
            )}

            {panel === 'layout' && (
              <LayoutPanel
                meta={meta}
                heroVariant={heroVariant}
                menuLayout={menuLayout}
                navStyle={navStyle}
                onHeroVariant={setHeroVariant}
                onMenuLayout={setMenuLayout}
                onNavStyle={setNavStyle}
              />
            )}

            {panel === 'images' && (
              <ImageLibrary images={images} onChange={setImages} legacyHero={hero} onMoveLegacyIntoLibrary={moveLegacyIntoLibrary} />
            )}

            {panel === 'page' && (
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="sf-headline">Headline</Label>
                  <Input
                    id="sf-headline"
                    maxLength={120}
                    placeholder="Charcoal grills and chai till late"
                    value={heroHeadline}
                    onChange={(e) => setHeroHeadline(e.target.value)}
                  />
                  <p className="text-[11px] text-muted-foreground">One line under your name at the top of the page.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sf-subcopy">Second line</Label>
                  <Input
                    id="sf-subcopy"
                    maxLength={240}
                    placeholder="Order ahead and skip the wait."
                    value={heroSubcopy}
                    onChange={(e) => setHeroSubcopy(e.target.value)}
                  />
                  <p className="text-[11px] text-muted-foreground">Blank shows nothing, not a stock line.</p>
                </div>
                <div className="border-t pt-5">
                  <SectionsEditor
                    sections={sections}
                    onChange={setSections}
                    announcementText={announcementText}
                    announcementUrl={announcementUrl}
                    onAnnouncementText={setAnnouncementText}
                    onAnnouncementUrl={setAnnouncementUrl}
                    about={about}
                    onAbout={setAbout}
                    imageCount={images.length}
                  />
                </div>
              </div>
            )}

            {panel === 'details' && (
              <div className="space-y-5">
                <SectionTitle title="On a customer's phone" hint="What they see once they add your site to their home screen." />
                <IdentityCard
                  pwaShortName={pwaShortName}
                  appIconUrl={appIconUrl}
                  onPwaShortName={setPwaShortName}
                  onAppIconUrl={setAppIconUrl}
                />
                <div className="space-y-2">
                  <div className="flex items-baseline justify-between">
                    <Label htmlFor="sf-splash">Loading phrase</Label>
                    <span className="text-[11px] tabular-nums text-muted-foreground">{splashPhrase.length}/40</span>
                  </div>
                  <Input
                    id="sf-splash"
                    maxLength={40}
                    placeholder="firing up the tandoor"
                    value={splashPhrase}
                    onChange={(e) => setSplashPhrase(e.target.value)}
                  />
                  <p className="text-[11px] text-muted-foreground">Shown on the launch screen while your store loads. Blank for none.</p>
                </div>

                <div className="space-y-4 border-t pt-5">
                  <SectionTitle title="When your link is shared" hint="The title and line shown on WhatsApp, Google and the browser tab." />
                  <div className="space-y-2">
                    <div className="flex items-baseline justify-between">
                      <Label htmlFor="sf-seo-title">Link title</Label>
                      <span className="text-[11px] tabular-nums text-muted-foreground">{seoTitle.length}/70</span>
                    </div>
                    <Input id="sf-seo-title" maxLength={70} placeholder={`${restaurantName}, order online`} value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} />
                    <p className="text-[11px] text-muted-foreground">Live on your storefront within a minute of saving.</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-baseline justify-between">
                      <Label htmlFor="sf-seo-desc">Link description</Label>
                      <span className="text-[11px] tabular-nums text-muted-foreground">{seoDescription.length}/160</span>
                    </div>
                    <Input
                      id="sf-seo-desc"
                      maxLength={160}
                      placeholder="Order online for pickup and delivery."
                      value={seoDescription}
                      onChange={(e) => setSeoDescription(e.target.value)}
                    />
                  </div>
                  <SeoPreview
                    name={restaurantName}
                    title={seoTitle}
                    description={seoDescription}
                    heroSubcopy={heroSubcopy}
                    imageUrl={images[0]?.src ?? null}
                    url={base}
                  />
                </div>

                <div className="space-y-2 border-t pt-5">
                  <Label htmlFor="sf-minorder">Minimum order value</Label>
                  <Input
                    id="sf-minorder"
                    type="number"
                    min={0}
                    inputMode="numeric"
                    placeholder="0 (no minimum)"
                    value={minOrder}
                    onChange={(e) => setMinOrder(e.target.value)}
                  />
                  <p className="text-[11px] text-muted-foreground">Customers see a nudge below this, and checkout is blocked.</p>
                </div>

                <div className="space-y-2 border-t pt-5">
                  <Label>Social links</Label>
                  <div className="space-y-2">
                    {socials.map((s, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <select
                          value={s.platform}
                          aria-label="Platform"
                          onChange={(e) => setSocials((arr) => arr.map((x, j) => (j === i ? { ...x, platform: e.target.value } : x)))}
                          className="h-10 rounded-md border border-input bg-transparent px-2 text-sm capitalize"
                        >
                          {SOCIAL_PLATFORMS.map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                        <Input
                          type="url"
                          inputMode="url"
                          placeholder="https://…"
                          value={s.url}
                          onChange={(e) => setSocials((arr) => arr.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
                          className="min-w-0 flex-1"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove ${s.platform} link`}
                          title="Remove"
                          onClick={() => setSocials((arr) => arr.filter((_, j) => j !== i))}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    {socials.length < 8 && (
                      <Button type="button" variant="outline" size="sm" onClick={() => setSocials((arr) => [...arr, { platform: 'instagram', url: '' }])}>
                        Add link
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <div
          className={cn(
            'min-h-0 min-w-0 flex-1 bg-muted/40 @2xl:block',
            view === 'preview' ? 'block h-[44rem] lg:h-auto' : 'hidden',
          )}
        >
          {/* Where the page itself scrolls, the preview rides along beside the controls. */}
          <div className="h-full max-lg:sticky max-lg:top-0 max-lg:max-h-[calc(var(--app-vh,100dvh)_-_1rem)]">
          <LivePreview
            base={base}
            onBase={setBase}
            design={design}
            openUrl={openUrl}
            opensDark={previewDark}
            fallback={(previewMode) => (
              <MockStorefront
                d={{
                  name: restaurantName,
                  logo: effLogo,
                  brand: effBrand,
                  accent: effAccent,
                  canvas:
                    previewMode === 'dark'
                      ? (HEX.test(canvasDark) ? canvasDark : meta.canvasDark)
                      : (HEX.test(canvasLight) ? canvasLight : meta.canvasLight),
                  fontHeading: fontHeading || meta.fontHeading,
                  fontBody: fontBody || meta.fontBody,
                  upper: (headingCase || meta.headingCase) === 'upper',
                  italic: !fontHeading && meta.headingItalic === true,
                  radius: radius || meta.radius,
                  buttonShape: buttonShape || meta.buttonShape,
                  cardStyle: cardStyle || meta.cardStyle,
                  navStyle: navStyle || meta.navStyle,
                  menuLayout: menuLayout || meta.menuLayout,
                  heroVariant: heroVariant || meta.heroVariant,
                  headline: heroHeadline,
                  subcopy: heroSubcopy,
                  photos: (images.length > 0 ? images.map((i) => i.src) : [hero.trim()]).filter(Boolean),
                }}
              />
            )}
          />
          </div>
        </div>
      </div>
    </div>
  )
}

function SectionTitle({ title, hint }: { title: string; hint: string }) {
  return (
    <div>
      <h4 className="text-sm font-semibold">{title}</h4>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  )
}
